// client/src/services/memoryPressure.ts
//
// GOOGLE PLAY QUALITY 2027 (Feb 2027) — pilastro "Dynamic memory usage".
//
// La metrica che Google misura (anonymous RSS + swap) include la memoria
// trattenuta quando l'app NON è visibile, per stato di processo (in uso /
// foreground service / cached). La documentazione ufficiale chiede di
// reagire ai callback di sistema:
//   - TRIM_MEMORY_BACKGROUND → svuotare le cache
//   - TRIM_MEMORY_UI_HIDDEN  → rilasciare le risorse grafiche
//
// Il lato nativo (MainActivity.onTrimMemory) inoltra il livello di pressione
// al WebView con un CustomEvent "rc:trim-memory"; questo modulo lo consuma,
// svuota le cache JS rigenerabili e notifica gli iscritti (es. le mappe, che
// possono distruggere la surface WebGL quando l'app non è visibile).
//
// Il modulo NON solleva mai eccezioni verso il chiamante: in caso di errore
// la pressione di memoria resta un'ottimizzazione best-effort.

import { clearMapCache } from "@/services/mapService";
import { clearAllTileCaches } from "@/services/offlineTileCache";

/** Nome dell'evento dispatchato dal nativo (MainActivity.onTrimMemory). */
export const MEMORY_PRESSURE_EVENT = "rc:trim-memory";

/**
 * Soglia minima per lo svuotamento delle cache JS.
 * Valori android.content.ComponentCallbacks2:
 *   10 RUNNING_LOW · 20 UI_HIDDEN · 40 BACKGROUND · 60 MODERATE · 80 COMPLETE
 * Sotto UI_HIDDEN (10) l'app è ancora visibile: svuotare le cache
 * costringerebbe a rifare richieste di rete senza alcun beneficio reale.
 */
export const CACHE_PURGE_MIN_LEVEL = 20;

type MemoryPressureListener = (level: number) => void;

const listeners = new Set<MemoryPressureListener>();
let lastLevel = 0;
let listening = false;

/** Livello di pressione più alto ricevuto (0 = nessuna pressione nota). */
export function getLastMemoryPressureLevel(): number {
  return lastLevel;
}

/**
 * Rilascia le risorse rigenerabili in risposta alla pressione di memoria.
 * Esportata per i test e per il wiring esplicito lato nativo.
 */
export function releaseCachesUnderPressure(level: number): void {
  if (level >= CACHE_PURGE_MIN_LEVEL) {
    try {
      // Cache TTL di routing/geocoding: rigenerata on demand, nessun impatto
      // UX (gli eventuali risultati in volo restano validi).
      clearMapCache();
      // Tile mappa: revoca i blob URL in memoria (budget LRU in offlineTileCache).
      // È la voce più pesante fra le cache rigenerabili: un blob URL non revocato
      // trattiene il Blob fino al reload del documento.
      clearAllTileCaches();
    } catch {
      /* best-effort */
    }
  }
}

/** Iscrive un handler di pressione memoria. Ritorna la funzione di unsubscribe. */
export function onMemoryPressure(listener: MemoryPressureListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Gestisce un evento di pressione memoria (esportata per i test). */
export function handleMemoryPressure(levelRaw: unknown): void {
  const level = Number(levelRaw);
  const safeLevel = Number.isFinite(level) ? level : 0;
  if (safeLevel > lastLevel) lastLevel = safeLevel;

  if (safeLevel > 0) {
    // Log deliberatamente sempre attivo: è il punto di osservazione usato per
    // verificare su device che il bridge nativo funzioni
    // (chrome://inspect → console WebView).
    console.debug(`[memoryPressure] level=${safeLevel} (max=${lastLevel})`);
  }

  releaseCachesUnderPressure(safeLevel);

  // Copia difensiva: un listener può disiscriversi durante la notifica.
  for (const listener of Array.from(listeners)) {
    try {
      listener(safeLevel);
    } catch {
      /* un listener rotto non deve bloccare gli altri */
    }
  }
}

/**
 * Attiva il bridge nativo → JS. Idempotente: chiamate ripetute non
 * duplicano il listener. Ritorna la funzione di cleanup.
 */
export function startMemoryPressureBridge(): () => void {
  if (listening) return () => {};
  listening = true;

  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ level?: number }>)?.detail;
    handleMemoryPressure(detail?.level);
  };

  try {
    window.addEventListener(MEMORY_PRESSURE_EVENT, handler);
  } catch {
    listening = false;
    return () => {};
  }

  return () => {
    try {
      window.removeEventListener(MEMORY_PRESSURE_EVENT, handler);
    } catch {
      /* best-effort */
    }
    listening = false;
  };
}

/** Solo per i test: ripristina lo stato iniziale del modulo. */
export function __resetMemoryPressureForTests(): void {
  listeners.clear();
  lastLevel = 0;
  listening = false;
}
